import { z } from 'zod';

/**
 * Vague 7 (ajout 10) : **modules activables**. Registre unique du produit : le serveur refuse les nouvelles
 * écritures d'un module désactivé (403 `module_disabled`, `MODULE_ROUTE_GATES` et contrôles des lots),
 * l'interface masque le module (menu, widgets, onglets, actions). Désactiver un module **n'efface aucune
 * donnée** : la lecture des données existantes reste possible dans les historiques ; un cycle en cours se
 * termine (voir `MODULES[*].whenDisabled`). Réactiver rend tout immédiatement.
 *
 * Stockage : `garage_settings.module_overrides` (écarts à la valeur par défaut seulement) ; un module ajouté
 * plus tard prend donc sa valeur par défaut dans chaque garage. Ajouter un module = une entrée ici.
 */

export const MODULE_KEYS = [
  // Noyau (jamais désactivable)
  'dossiers',
  'customer_portal',
  // Activables
  'entry_report',
  'photo_report',
  'complements',
  'customer_messaging',
  'central_inbox',
  'release_report',
  'storage',
  'planning',
  'agenda',
  'plate_scan',
  'push_notifications',
  'atelier_metrics',
  'weekly_digest',
  // À venir
  'pennylane',
  'sms',
  'maintenance_reminders',
  'vehicle_logbook',
] as const;
export const ModuleKey = z.enum(MODULE_KEYS);
export type ModuleKey = z.infer<typeof ModuleKey>;

/** Catégories du catalogue : Atelier, Client, Pilotage, Intégrations. */
export const ModuleCategory = z.enum(['workshop', 'customer', 'steering', 'integrations']);
export type ModuleCategory = z.infer<typeof ModuleCategory>;
export const MODULE_CATEGORY_LABELS: Record<ModuleCategory, string> = {
  workshop: 'Atelier',
  customer: 'Client',
  steering: 'Pilotage',
  integrations: 'Intégrations',
};

/** `available` : utilisable ; `coming_soon` : affiché « Bientôt », jamais activable. */
export const ModuleStatus = z.enum(['available', 'coming_soon']);
export type ModuleStatus = z.infer<typeof ModuleStatus>;

export interface ModuleDefinition {
  label: string;
  /** Texte commercial court et clair (carte du catalogue, plaquette). */
  description: string;
  /** Clé d'icône (l'interface la traduit en pictogramme). */
  icon: string;
  category: ModuleCategory;
  status: ModuleStatus;
  /** Noyau : toujours actif, jamais désactivable. */
  core: boolean;
  /** Valeur pour un garage qui n'a rien réglé. */
  defaultEnabled: boolean;
  /** Modules requis : activer celui-ci les active ; désactiver l'un d'eux désactive celui-ci. */
  dependsOn: readonly ModuleKey[];
  /** Comportement documenté quand le module est désactivé (données gardées, cycle en cours). */
  whenDisabled: string;
}

export const MODULES: Record<ModuleKey, ModuleDefinition> = {
  dossiers: {
    label: 'Dossiers et suivi d’atelier',
    description: 'Entrée des véhicules, statuts, travaux réalisés, historique chiffré de chaque véhicule.',
    icon: 'folder',
    category: 'workshop',
    status: 'available',
    core: true,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled: 'Noyau : toujours actif.',
  },
  customer_portal: {
    label: 'Lien de suivi client',
    description: 'Une page aux couleurs du garage, sans compte ni application, pour suivre son véhicule.',
    icon: 'link',
    category: 'customer',
    status: 'available',
    core: true,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled: 'Noyau : toujours actif.',
  },
  entry_report: {
    label: 'Fiche de réception',
    description: 'État du véhicule à l’arrivée, dommages photographiés, signature et PDF remis au client.',
    icon: 'clipboard-check',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled:
      'Plus de nouveau brouillon à l’entrée ni de validation ou d’envoi. Les fiches validées restent consultables (garage et client) et la prise de connaissance d’une fiche déjà partagée reste possible.',
  },
  photo_report: {
    label: 'Reportage photo',
    description: 'Photos prises pendant l’intervention, publiées au client sans GPS ni métadonnées.',
    icon: 'camera',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled:
      'Plus d’envoi ni de publication de photos de reportage. Les photos déjà publiées restent visibles ; retirer une photo de la vue client ou la supprimer reste possible.',
  },
  complements: {
    label: 'Travaux complémentaires',
    description: 'Proposez des travaux en cours d’intervention ; le client accepte en ligne, la version exacte fait foi.',
    icon: 'file-plus',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled:
      'Plus de nouveaux travaux complémentaires ni d’envoi. Une version déjà envoyée suit son cycle : le client peut encore décider, le garage saisir une décision ou annuler.',
  },
  customer_messaging: {
    label: 'Messagerie client',
    description: 'Le client pose ses questions et envoie ses photos depuis son lien de suivi ; vous répondez au même endroit.',
    icon: 'message',
    category: 'customer',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled:
      'Le client ne peut plus écrire ni envoyer de photo. Le garage peut encore répondre aux questions déjà posées ; les conversations restent lisibles.',
  },
  central_inbox: {
    label: 'Messagerie centrale',
    description: 'Toutes les conversations du garage au même endroit, avec les non-lus de chacun.',
    icon: 'inbox',
    category: 'customer',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: ['customer_messaging'],
    whenDisabled: 'La messagerie centrale disparaît du menu ; les conversations restent dans chaque dossier.',
  },
  release_report: {
    label: 'Compte rendu permanent',
    description: 'À la sortie, un compte rendu consultable par le client pendant des mois : travaux, photos, historique.',
    icon: 'file-text',
    category: 'customer',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled: 'Plus de nouveau lien de compte rendu à la sortie. Les liens déjà remis restent valables jusqu’à leur échéance.',
  },
  storage: {
    label: 'Gardiennage',
    description: 'Délai gratuit annoncé à l’entrée, rappel au client, dépassement signalé, frais offerts tracés.',
    icon: 'warehouse',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled:
      'Plus d’indicateur de dépassement, de rappel ni de remise de frais. Les conditions déjà figées à l’entrée restent dans les dossiers.',
  },
  planning: {
    label: 'Planification et arrivées prévues',
    description: 'Planifiez les arrivées, retrouvez-les à l’Accueil et faites entrer le véhicule en un geste.',
    icon: 'calendar-clock',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled:
      'Plus de nouvelle planification ni de modification. Les rendez-vous déjà planifiés restent visibles et peuvent encore être annulés ou entrés.',
  },
  agenda: {
    label: 'Agenda',
    description: 'Planning de l’atelier par mécanicien ou par pont, avec les créneaux des véhicules.',
    icon: 'calendar',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: false,
    dependsOn: ['planning'],
    whenDisabled: 'L’agenda disparaît. Les créneaux déjà posés restent enregistrés et réapparaissent à la réactivation.',
  },
  plate_scan: {
    label: 'Scan de plaque',
    description: 'Lisez la plaque avec l’appareil photo du téléphone pour retrouver ou créer le véhicule.',
    icon: 'scan',
    category: 'workshop',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled: 'Le bouton de lecture de plaque disparaît ; la saisie manuelle reste.',
  },
  push_notifications: {
    label: 'Notifications sur téléphone',
    description: 'L’équipe est prévenue sur son téléphone quand un client écrit.',
    icon: 'bell',
    category: 'customer',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: ['customer_messaging'],
    whenDisabled: 'Plus de nouvel abonnement ni d’envoi de notification ; les abonnements existants sont gardés et reprennent à la réactivation.',
  },
  atelier_metrics: {
    label: 'Chiffres de l’atelier',
    description: 'Travaux complémentaires acceptés, taux et délai de décision, travaux réalisés : le mois et la tendance sur un an.',
    icon: 'chart',
    category: 'steering',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: [],
    whenDisabled: 'Le widget et la page des chiffres disparaissent ; rien n’est effacé (les chiffres sont recalculés à la demande).',
  },
  weekly_digest: {
    label: 'Bilan hebdomadaire',
    description: 'Chaque lundi à 8 h, un e-mail au propriétaire avec les chiffres et les points en attente de la semaine.',
    icon: 'mail',
    category: 'steering',
    status: 'available',
    core: false,
    defaultEnabled: true,
    dependsOn: ['atelier_metrics'],
    whenDisabled: 'Plus aucun bilan envoyé. Les bilans déjà envoyés restent dans le journal des envois.',
  },
  pennylane: {
    label: 'Export et connexion Pennylane',
    description: 'Vos travaux acceptés vers Pennylane, et vos encaissements relus pour les chiffres de l’atelier.',
    icon: 'plug',
    category: 'integrations',
    status: 'coming_soon',
    core: false,
    defaultEnabled: false,
    dependsOn: [],
    whenDisabled: 'Bientôt disponible.',
  },
  sms: {
    label: 'SMS au client',
    description: 'Les moments clés du dossier aussi par SMS, avec des textes que vous choisissez.',
    icon: 'sms',
    category: 'customer',
    status: 'coming_soon',
    core: false,
    defaultEnabled: false,
    dependsOn: [],
    whenDisabled: 'Bientôt disponible.',
  },
  maintenance_reminders: {
    label: 'Rappel de prochaine échéance',
    description:
      'Un e-mail chaleureux au client quand la prochaine échéance saisie au compte rendu approche (révision, contrôle…), pour qu’il reprenne rendez-vous.',
    icon: 'wrench',
    category: 'customer',
    status: 'available',
    core: false,
    // 08/10/2026 : e-mail automatique au client, envoyé des mois après la sortie : activé par le garage.
    defaultEnabled: false,
    dependsOn: [],
    whenDisabled:
      'Plus aucun rappel planifié ni envoyé ; un rappel déjà inscrit et pas encore parti est annulé. Les rappels envoyés restent dans le journal des envois.',
  },
  vehicle_logbook: {
    label: 'Carnet du véhicule',
    description: 'Un carnet d’entretien partagé avec le client, qui suit le véhicule d’intervention en intervention.',
    icon: 'book',
    category: 'customer',
    status: 'coming_soon',
    core: false,
    defaultEnabled: false,
    dependsOn: [],
    whenDisabled: 'Bientôt disponible.',
  },
};

/** Écarts à la valeur par défaut, tels que stockés (`garage_settings.module_overrides`). */
export const ModuleOverrides = z.record(z.string(), z.boolean());
export type ModuleOverrides = z.infer<typeof ModuleOverrides>;

/** Modules qui dépendent (directement) de `key`. */
export function moduleDependents(key: ModuleKey): ModuleKey[] {
  return MODULE_KEYS.filter((k) => MODULES[k].dependsOn.includes(key));
}

/**
 * Modules actifs d'un garage : noyau toujours ; « à venir » jamais ; sinon l'écart enregistré ou la valeur
 * par défaut ; puis un module dont une dépendance est inactive est inactif (jusqu'à stabilité). Une clé
 * inconnue (module retiré) est ignorée.
 */
export function resolveEnabledModules(overrides: ModuleOverrides | null | undefined): Set<ModuleKey> {
  const enabled = new Set<ModuleKey>();
  for (const key of MODULE_KEYS) {
    const def = MODULES[key];
    if (def.core) enabled.add(key);
    else if (def.status === 'available' && (overrides?.[key] ?? def.defaultEnabled)) enabled.add(key);
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const key of [...enabled]) {
      if (MODULES[key].dependsOn.some((d) => !enabled.has(d))) {
        enabled.delete(key);
        changed = true;
      }
    }
  }
  return enabled;
}

export interface ModuleChange {
  key: ModuleKey;
  enabled: boolean;
  /** `requested` : demandé ; `dependency` : conséquence d'une dépendance (avertissement à l'écran). */
  cause: 'requested' | 'dependency';
}

/**
 * Applique une demande de changement : activer un module active ses dépendances ; désactiver un module
 * désactive ceux qui en dépendent. Renvoie les nouveaux écarts (normalisés : seulement ce qui diffère de
 * la valeur par défaut) et la liste des changements effectifs. Lève une erreur (message en français) pour
 * un module du noyau désactivé ou un module « à venir » activé.
 */
export function applyModuleChanges(
  overrides: ModuleOverrides | null | undefined,
  requested: Partial<Record<ModuleKey, boolean>>,
): { overrides: Record<string, boolean>; changes: ModuleChange[] } {
  const before = resolveEnabledModules(overrides);
  const wanted = new Map<ModuleKey, boolean>();
  for (const key of MODULE_KEYS) {
    const def = MODULES[key];
    if (!def.core && def.status === 'available') wanted.set(key, before.has(key));
  }
  const requestedKeys = new Set<ModuleKey>();
  for (const [k, value] of Object.entries(requested) as [ModuleKey, boolean | undefined][]) {
    if (value === undefined) continue;
    const def = MODULES[k];
    if (def.core && !value) throw new ModuleChangeError(`« ${def.label} » fait partie du noyau : il ne peut pas être désactivé.`);
    if (def.status === 'coming_soon' && value) throw new ModuleChangeError(`« ${def.label} » n’est pas encore disponible.`);
    if (def.core || def.status !== 'available') continue;
    wanted.set(k, value);
    requestedKeys.add(k);
  }
  // Activer : dépendances activées (transitivement).
  const enableDeps = (k: ModuleKey) => {
    for (const d of MODULES[k].dependsOn) {
      if (wanted.has(d) && !wanted.get(d)) {
        wanted.set(d, true);
        enableDeps(d);
      }
    }
  };
  // Désactiver : dépendants désactivés (transitivement).
  const disableDependents = (k: ModuleKey) => {
    for (const dep of moduleDependents(k)) {
      if (wanted.get(dep)) {
        wanted.set(dep, false);
        disableDependents(dep);
      }
    }
  };
  for (const k of requestedKeys) if (wanted.get(k)) enableDeps(k);
  for (const k of requestedKeys) if (!wanted.get(k)) disableDependents(k);

  const next: Record<string, boolean> = {};
  for (const [k, value] of wanted) if (value !== MODULES[k].defaultEnabled) next[k] = value;
  const after = resolveEnabledModules(next);
  const changes: ModuleChange[] = [];
  for (const key of MODULE_KEYS) {
    if (before.has(key) !== after.has(key)) {
      changes.push({ key, enabled: after.has(key), cause: requestedKeys.has(key) ? 'requested' : 'dependency' });
    }
  }
  return { overrides: next, changes };
}

export class ModuleChangeError extends Error {}

export const ModuleOutput = z.object({
  key: ModuleKey,
  label: z.string(),
  description: z.string(),
  icon: z.string(),
  category: ModuleCategory,
  status: ModuleStatus,
  core: z.boolean(),
  defaultEnabled: z.boolean(),
  dependsOn: z.array(ModuleKey),
  /** Modules désactivés en même temps que celui-ci (avertissement avant confirmation). */
  dependents: z.array(ModuleKey),
  whenDisabled: z.string(),
  enabled: z.boolean(),
});
export type ModuleOutput = z.infer<typeof ModuleOutput>;

export const ModulesOutput = z.object({
  modules: z.array(ModuleOutput),
  /** Modules actifs du garage (même information que `modules[*].enabled`, pratique pour filtrer). */
  enabledModules: z.array(ModuleKey),
});
export type ModulesOutput = z.infer<typeof ModulesOutput>;

export const ModulesUpdateInput = z.strictObject({
  modules: z.partialRecord(ModuleKey, z.boolean()).refine((m) => Object.keys(m).length > 0, 'Aucun module indiqué'),
});

export const ModulesUpdateOutput = ModulesOutput.extend({
  changes: z.array(z.object({ key: ModuleKey, enabled: z.boolean(), cause: z.enum(['requested', 'dependency']) })),
});
export type ModulesUpdateOutput = z.infer<typeof ModulesUpdateOutput>;

/** Catalogue et état, dans l'ordre du registre. */
export function modulesOutput(overrides: ModuleOverrides | null | undefined): ModulesOutput {
  const enabled = resolveEnabledModules(overrides);
  return {
    modules: MODULE_KEYS.map((key) => {
      const def = MODULES[key];
      return {
        key,
        label: def.label,
        description: def.description,
        icon: def.icon,
        category: def.category,
        status: def.status,
        core: def.core,
        defaultEnabled: def.defaultEnabled,
        dependsOn: [...def.dependsOn],
        dependents: moduleDependents(key),
        whenDisabled: def.whenDisabled,
        enabled: enabled.has(key),
      };
    }),
    enabledModules: MODULE_KEYS.filter((k) => enabled.has(k)),
  };
}
